import {useAuth} from '@apps-common/auth-react';
import {createSelector} from '@reduxjs/toolkit';
import isEqual from 'lodash/isEqual';
import {array, bool, func, number, object, string} from 'prop-types';
import {useEffect} from 'react';
import {connect} from 'react-redux';

import {
  handleWorkerMessage,
  resetResults,
  updateStatus,
} from '../actions/results.js';
import {getAvailableItemTypes} from '../selectors/item-types.js';
import {
  getGeometryTooLarge,
  getSearchFiltersObjForWorker,
  getSearchGeometryGeoJson,
  getSearchItemTypeIds,
} from '../selectors/search.js';
import modifyEndDate from '../util/modifyEndDate.js';
import pluckQueryParam from '../util/pluck-query-param.js';
import {resetSearchPageNumber} from '@explorer/actions/search';

const worker = new Worker(new URL('../worker/main.js', import.meta.url), {
  type: 'module',
});

const isDataReady = createSelector(
  [getAvailableItemTypes, getSearchGeometryGeoJson, getGeometryTooLarge],
  (itemTypes, geometry, geometryTooLarge) => {
    return !!(itemTypes.types.length > 0 && geometry && !geometryTooLarge);
  }
);

function WorkerManager({
  resetResults,
  resetSearchPageNumber,
  handleWorkerMessage,
  updateStatus,
  ...props
}) {
  const auth = useAuth();

  const isReady = props.ready && !!props.searchFilters && !!props.geometry;

  useEffect(() => {
    const onWorkerMessage = event => handleWorkerMessage(event.data);

    worker.addEventListener('message', onWorkerMessage);
    return () => {
      worker.removeEventListener('message', onWorkerMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.geometry) {
      worker.postMessage({
        type: 'clear',
      });
    }
  }, [props.geometry]);

  // tracks whether items have been orphaned.
  const orphanedItems = Boolean(
    (props.viewItems.length && !props.viewResults.length) ||
      (props.orderItems.length && !props.orderResults.length)
  );

  useEffect(() => {
    if (isReady) {
      // If we have any orphaned items (those without results), we
      // can reset the results and page index.
      // This check is needed to be able to preserve selected items/results when reloading or
      // sharing an URL.
      if (!orphanedItems) {
        resetResults();
        resetSearchPageNumber();
      }

      updateStatus(true);
      worker.postMessage({
        type: 'clear',
      });

      // if there is a since query param, we override the filter to include
      // imagery up to today.  And share the date with the worker so it can
      // mark imagery as new if it is more than that date
      const since = pluckQueryParam('since');

      const searchFilters = since
        ? modifyEndDate(JSON.parse(JSON.stringify(props.searchFilters)))
        : props.searchFilters;

      worker.postMessage({
        type: 'request',
        data: {
          filters: searchFilters,
          geometry: props.geometry,
          itemTypes: props.itemTypeIds,
          since: since && new Date(since),
          sortDirection: props.sortDirection,
          token: auth.user.accessToken,
          featureReference: props.featureReference,
        },
      });
    }
  }, [
    auth.user.accessToken,
    isReady,
    orphanedItems,
    props.featureReference,
    props.geometry,
    props.itemTypeIds,
    props.searchFilters,
    props.sortDirection,
    resetResults,
    resetSearchPageNumber,
    updateStatus,
  ]);

  useEffect(() => {
    if (isReady && props.searchPage > 0) {
      worker.postMessage({type: 'more'});
    }
  }, [isReady, props.searchPage]);

  return null;
}

WorkerManager.propTypes = {
  featureReference: string,
  geometry: object,
  handleWorkerMessage: func,
  itemTypeIds: array,
  orderItems: array,
  orderResults: array,
  ready: bool.isRequired,
  resetResults: func,
  resetSearchPageNumber: func,
  searchFilters: object,
  searchName: string,
  searchPage: number,
  sortDirection: string,
  updateStatus: func,
  viewItems: array,
  viewResults: array,
};

const mapStateToProps = state => ({
  geometry: getSearchGeometryGeoJson(state),
  itemTypeIds: getSearchItemTypeIds(state),
  orderItems: state.results.orderItems,
  orderResults: state.results.orderResults,
  ready: isDataReady(state),
  searchFilters: getSearchFiltersObjForWorker(state),
  searchName: state.search.name,
  searchPage: state.search.page,
  sortDirection: state.sidebar.resultsSortDirection,
  viewItems: state.results.viewItems,
  viewResults: state.results.viewResults,
  featureReference: state.search.featureReference,
});

const mapDispatchProps = {
  handleWorkerMessage,
  resetResults,
  resetSearchPageNumber,
  updateStatus,
};

const connectOpts = {
  areStatePropsEqual: (next, prev) => {
    return (
      next.ready === prev.ready &&
      isEqual(next.searchFilters, prev.searchFilters) &&
      isEqual(next.geometry, prev.geometry) &&
      next.searchPage === prev.searchPage &&
      isEqual(next.itemTypeIds, prev.itemTypeIds) &&
      next.sortDirection === prev.sortDirection
    );
  },
};

export default connect(
  mapStateToProps,
  mapDispatchProps,
  undefined,
  connectOpts
)(WorkerManager);
